import * as React from 'react';

import type {ListOptionWithIcon} from 'shells/select-option-components/option-with-icon';

import type {NotifiableAliasType} from 'nutshell-graphql-types';
import type {
    DripSequenceFragment as DripSequence,
    DripSequenceGoalSetInput,
    SmsDripSequenceFragment as SmsDripSequence,
    DripSequenceGoalSetInputFieldInput,
} from 'nutshell-graphql-types';

import {GoalCard} from '../goal/goal-card';
import {AddGoalForm} from '../sidebar/add-goal-form';
import type {GoalTypeValue} from '../sidebar/constants';
import {DripSequenceSidebarForm} from '../sidebar/drip-sequence-sidebar-form';
import {formDataToGoalConditionsArray} from '../sidebar/helpers';

import type {Variant} from './color-icon-variants';

type User = {
    id: string;
    name: string;
};

type Assignee = {
    type: NotifiableAliasType;
};

type Props = {
    isLoading: boolean;
    dripSequence: DripSequence | SmsDripSequence | null | undefined;
    onSetGoal: (input: DripSequenceGoalSetInput) => Promise<any>;
    onDripSequenceUpdate: (input: {id: string; isStopOnGoal: boolean}) => Promise<any>;
    variant?: Variant;
    notifiableUsers?: User[];
    notifiableAssignees?: Assignee[];
    goalTypeValues: GoalTypeValue[];
};

export function GoalCardWrapper(props: Props) {
    const dripSequence = props.dripSequence;

    const [isEditingGoal, setIsEditingGoal] = React.useState<string | null | undefined>(null);
    const [isNotifyUsersOnGoalMet, setIsNotifyUsersOnGoalMet] = React.useState<boolean>(false);
    const [selectedUserIds, setSelectedUserIds] = React.useState<string[]>(
        props.notifiableUsers ? props.notifiableUsers.map((user) => user.id) : []
    );
    const [selectedAssigneeValues, setSelectedAssigneeValues] = React.useState<
        NotifiableAliasType[]
    >(props.notifiableAssignees ? props.notifiableAssignees.map((assignee) => assignee.type) : []);
    const [isStopOnGoalMet, setIsStopOnGoalMet] = React.useState<boolean>(false);
    const [isSaving, setIsSaving] = React.useState<boolean>(false);
    const [saveFailedErrorMessage, setSaveFailedErrorMessage] = React.useState<
        string | null | undefined
    >(undefined);

    React.useEffect(() => {
        setIsStopOnGoalMet(
            dripSequence && dripSequence.isStopOnGoal ? Boolean(dripSequence.isStopOnGoal) : false
        );
    }, [dripSequence]);

    const saveGoal = (
        dripSequenceId: string,
        variables: DripSequenceGoalSetInputFieldInput | null | undefined,
        stopOnGoalMet: boolean
    ): Promise<any> => {
        const setGoalPromise = props.onSetGoal({
            campaignId: dripSequenceId,
            dripSequenceGoal: variables,
        });

        const updateCampaignWithStopOnGoalPromise = props.onDripSequenceUpdate({
            id: dripSequenceId,
            isStopOnGoal: stopOnGoalMet,
        });

        return Promise.all([setGoalPromise, updateCampaignWithStopOnGoalPromise])
            .then(() => {
                setIsEditingGoal(null);
            })
            .catch((error) => {
                setSaveFailedErrorMessage(error.message);
            });
    };

    return (
        <div className='mb-16'>
            <GoalCard
                isLoading={props.isLoading}
                goal={dripSequence ? dripSequence.goal : undefined}
                onClick={(goalId: string) => setIsEditingGoal(goalId)}
                variant={props.variant}
            />
            {isEditingGoal && dripSequence ? (
                <DripSequenceSidebarForm
                    title={dripSequence.goal ? 'Edit goal' : 'Add goal'}
                    // @ts-expect-error
                    onSubmit={(formData) => {
                        if (!formData.goalType) return;

                        const variables = {
                            type: formData.goalType,
                            conditions: formDataToGoalConditionsArray(formData),
                        } as const;

                        return saveGoal(dripSequence.id, variables, isStopOnGoalMet);
                    }}
                    onClose={() => {
                        setSaveFailedErrorMessage(undefined);
                        setIsEditingGoal(null);
                    }}
                    isNewItem={Boolean(!props.dripSequence || !props.dripSequence.goal)}
                    existingGoal={
                        props.dripSequence && props.dripSequence.goal
                            ? props.dripSequence.goal
                            : undefined
                    }
                    isSaving={isSaving}
                    saveFailedErrorMessage={saveFailedErrorMessage}
                    // @ts-expect-error
                    deleteProps={
                        dripSequence && dripSequence.goal
                            ? {
                                  title: 'Remove goal',
                                  buttonText: 'Remove goal',
                                  message: 'Are you sure you’d like to remove this goal?',
                                  onDelete: () => {
                                      setIsSaving(true);
                                      saveGoal(dripSequence.id, null, false).then(() => {
                                          setIsSaving(false);
                                      });
                                  },
                              }
                            : undefined
                    }
                >
                    <AddGoalForm
                        isNotifyUsersOnGoalMet={isNotifyUsersOnGoalMet}
                        setIsNotifyUsersOnGoalMet={(newValue: boolean) =>
                            setIsNotifyUsersOnGoalMet(newValue)
                        }
                        onSelectUsers={(selection: ListOptionWithIcon[]) => {
                            if (!selection) {
                                setSelectedUserIds([]);
                                setSelectedAssigneeValues([]);

                                return;
                            }

                            const selectedValues = selection.map((user) => user.value);
                            const userIds = selectedValues.filter(
                                (value) =>
                                    value !== 'CONTACT_ASSIGNEE' &&
                                    value !== 'RELATED_LEAD_ASSIGNEE'
                            );
                            const assigneeValues = selectedValues.filter(
                                (value) =>
                                    value === 'CONTACT_ASSIGNEE' ||
                                    value === 'RELATED_LEAD_ASSIGNEE'
                            );

                            setSelectedUserIds(userIds);
                            setSelectedAssigneeValues(assigneeValues);
                        }}
                        onClearSelectedUsers={() => {
                            setSelectedUserIds([]);
                            setSelectedAssigneeValues([]);
                        }}
                        notifiableUserIds={selectedUserIds}
                        notifiableAssignees={selectedAssigneeValues}
                        isStopOnGoalMet={isStopOnGoalMet}
                        // @ts-expect-error
                        setIsStopOnGoalMet={(newValue) => setIsStopOnGoalMet(newValue)}
                        dripSequenceId={dripSequence.id}
                        goalTypeValues={props.goalTypeValues}
                    />
                </DripSequenceSidebarForm>
            ) : undefined}
        </div>
    );
}
